<template>
  <div class="entity-list">
    <div class="container py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1>{{ formattedEntityType }}</h1>
        <button class="btn btn-primary" @click="showCreateModal = true">
          <i class="bi bi-plus-circle me-2"></i>Create New
        </button>
      </div>
      
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading {{ entityType }}...</p>
      </div>
      
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        {{ error }}
      </div>
      
      <div v-else-if="entities.length === 0" class="text-center py-5">
        <div class="alert alert-info" role="alert">
          <i class="bi bi-info-circle me-2"></i>
          No {{ entityType }} found. Click the "Create New" button to add one.
        </div>
      </div>
      
      <div v-else class="card shadow">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th v-for="header in tableHeaders" :key="header.key" scope="col">
                  {{ header.label }}
                </th>
                <th scope="col" class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entity in entities" :key="entity.id">
                <td v-for="header in tableHeaders" :key="`${entity.id}-${header.key}`">
                  {{ entity[header.key] }}
                </td>
                <td class="text-end">
                  <div class="btn-group">
                    <router-link 
                      :to="`/${entityType}/${entity.id}`" 
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="bi bi-eye me-1"></i>View
                    </router-link>
                    <button 
                      class="btn btn-sm btn-outline-danger" 
                      @click="confirmDelete(entity)"
                    >
                      <i class="bi bi-trash me-1"></i>Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <!-- Create Modal -->
    <div v-if="showCreateModal" class="modal d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create New {{ formattedEntityType }}</h5>
            <button type="button" class="btn-close" @click="showCreateModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="createEntity">
              <div v-for="field in entityFields" :key="field.key" class="mb-3">
                <label :for="field.key" class="form-label">{{ field.label }}</label>
                <input 
                  :type="field.type" 
                  class="form-control" 
                  :id="field.key" 
                  v-model="newEntity[field.key]" 
                  :required="field.required"
                >
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCreateModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="createEntity" :disabled="creatingEntity">
              <span v-if="creatingEntity" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Create
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="entityToDelete" class="modal d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button type="button" class="btn-close" @click="entityToDelete = null"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this {{ entityType }}?</p>
            <p class="text-danger">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="entityToDelete = null">Cancel</button>
            <button type="button" class="btn btn-danger" @click="deleteEntity" :disabled="deletingEntity">
              <span v-if="deletingEntity" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'EntityListView',
  data() {
    return {
      showCreateModal: false,
      entityToDelete: null,
      newEntity: {},
      creatingEntity: false,
      deletingEntity: false,
      // These would ideally come from API metadata or be configured per entity type
      entityFields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'description', label: 'Description', type: 'text', required: false }
      ],
      tableHeaders: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' }
      ]
    };
  },
  computed: {
    ...mapGetters(['getEntities', 'isLoading', 'hasError', 'getError']),
    entityType() {
      return this.$route.params.entityType;
    },
    formattedEntityType() {
      return this.entityType.charAt(0).toUpperCase() + this.entityType.slice(1);
    },
    entities() {
      return this.getEntities(this.entityType);
    },
    loading() {
      return this.isLoading;
    },
    error() {
      return this.getError;
    }
  },
  methods: {
    ...mapActions(['fetchEntities', 'createEntity', 'deleteEntity']),
    async loadEntities() {
      try {
        await this.fetchEntities(this.entityType);
      } catch (error) {
        console.error('Failed to load entities:', error);
      }
    },
    async createEntity() {
      this.creatingEntity = true;
      try {
        await this.createEntity({
          entityType: this.entityType,
          data: this.newEntity
        });
        this.showCreateModal = false;
        this.newEntity = {};
      } catch (error) {
        console.error('Failed to create entity:', error);
      } finally {
        this.creatingEntity = false;
      }
    },
    confirmDelete(entity) {
      this.entityToDelete = entity;
    },
    async deleteEntity() {
      this.deletingEntity = true;
      try {
        await this.deleteEntity({
          entityType: this.entityType,
          id: this.entityToDelete.id
        });
        this.entityToDelete = null;
      } catch (error) {
        console.error('Failed to delete entity:', error);
      } finally {
        this.deletingEntity = false;
      }
    }
  },
  created() {
    this.loadEntities();
  },
  watch: {
    entityType() {
      this.loadEntities();
    }
  }
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
